import React from 'react';
import classNames from 'classnames';
import { Tooltip, Tag as ATag, TagProps as AntTagProps } from 'antd';
import cn from 'components/Tag/styles.module.scss';

export type TagType = {
  id: number,
  name: string,
  emoji?: string | null,
  description?: string | null,
  editable?: boolean,
};

export type TagVariant = 'filled' | 'outlined';
export type TagSize = 'small' | 'medium';

export type TagProps = Omit<AntTagProps, 'onClick' | 'onClose'> & {
  tag: TagType,
  maxLength?: number,
  closable?: boolean,
  active?: boolean,
  onClose?: (tagId: number) => void,
  onClick?: (tag: TagType) => void,
  className?: string,
  color?: string,
  variant?: TagVariant,
  bold?: boolean,
  bordered?: boolean,
  size?: TagSize,
  'data-testid'?: string,
};

export const Tag = React.forwardRef<HTMLSpanElement, TagProps>((
  {
    tag,
    maxLength,
    onClose,
    onClick,
    color,
    className,
    active,
    variant = 'outlined',
    bold,
    bordered = true,
    size = 'small',
    'data-testid': dataTestId,
    ...props
  },
  ref
) => {
  const text = tag.name;
  const isLongTag = maxLength ? text.length > maxLength : false;

  const handleClose = () => {
    if (onClose) {
      onClose(tag.id);
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick(tag);
    }
  };

  const getCustomStyle = (): React.CSSProperties => {
    const baseStyle = props.style || {};

    if (!color) {
      if (!bordered) {
        baseStyle.borderColor = '#fff';
      }
      return baseStyle;
    }

    if (variant === 'filled') {
      const bgColor = color;
      return {
        ...baseStyle,
        backgroundColor: bgColor,
        borderColor: bordered ? color : bgColor,
        color: 'white',
      };
    }

    // outlined variant
    const bgColor = `color-mix(in srgb, ${color} 10%, white)`;
    return {
      ...baseStyle,
      borderColor: bordered ? color : bgColor,
      color: color,
      backgroundColor: bgColor,
    };
  };

  const customStyle = getCustomStyle();

  const tagElement = (
    <ATag
      ref={ref}
      className={classNames(
        cn.tagWrapper,
        cn.tag,
        className,
        { [cn.active]: active },
        { [cn.filled]: variant === 'filled' },
        { [cn.outlined]: variant === 'outlined' },
        { [cn.bold]: bold },
        { [cn.small]: size === 'small' },
        { [cn.medium]: size === 'medium' },
      )}
      onClose={handleClose}
      onClick={handleClick}
      data-testid={dataTestId}
      {...props}
      style={customStyle}
    >
      {tag.emoji && <span>{tag.emoji}</span>}
      <span>
        {isLongTag ? `${text.slice(0, maxLength)}...` : text}
      </span>
    </ATag>
  );

  if (tag.description || isLongTag) {
    const title = tag.description ? `${text}: ${tag.description}` : text;
    return (
      <Tooltip
        title={title}
        key={text}
      >
        {tagElement}
      </Tooltip>
    );
  }

  return tagElement;
});

Tag.displayName = 'Tag';

export default Tag;
